import {minuteInMS, OnPopulated} from "web-vcore";
import {Muse_eegSamplesPerSecond_raw} from "../../../UI/Tools/@Shared/MuseInterface/EEGStructs.js";
import {Muse_gyroSamplesPerSecond_raw} from "../../../UI/Tools/@Shared/MuseInterface/GyroStructs.js";
import {DreamQuiz_QuestionType} from "./@EngineConfig/@EC_DreamQuiz.js";
import {GyroChannel} from "./@EngineConfig/@EC_Gyro.js";
import {AutoEndType, EngineConfig} from "./@EngineConfig.js";
import {prompt_storyExplore_1} from "./@LLMPrompts/Prompts_StoryExplore.js";
import {ThinkingType, EffortLevel} from "./@EngineConfig/@EC_StoryExplore.js";
import {Sequence, SequenceItem, TriggerSet} from "./@TriggerSet.js";
import {TranscribeTarget} from "./@EngineConfig/@EC_DreamTranscribe.js";
import {LongInhaleDetectionConfig} from "./@EngineConfig/@EC_Breath.js";
import {AlarmSequence, AlarmType} from "./@EngineConfig/Alarms/@AlarmConfig.js";
import {OptionPoolLayer, OptionPoolOrdering, OptionPoolSource} from "./@EngineConfig/@EC_Memory.js";
import {EngineConfig_SoundAlarm} from "./@EngineConfig/Alarms/@EC_BasicAlarms.js";
import {EffectPointer, SoundMutingType, SoundPanningType} from "../@Shared/EffectPointer.js";
import {Setting} from "./@EngineConfig/@Shared/Setting.js";
import {DreamFlawType, FramePool, ImageFrameFlawType} from "./@EngineConfig/@EC_VR.js";
import {DesyncCalcType, PanDirection, SirenType} from "./@EngineConfig/@EC_Breath.js";
import {EngineConfigVariant} from "./@EngineConfig/@Shared/EngineConfigVariant.js";
import {PoolEntrySelectionType} from "./@EngineConfig/@EC_MemoryPalace.js";
import {PromptPool, TTSVoiceType, VisPromptWeightGroup, VisualizationPromptPoolType} from "./@EngineConfig/@EC_Visualization.js";
import {Phase} from "../@Shared/Phase.js";

export let EngineConfig_GetDefault_static: EngineConfig;
OnPopulated(()=>{
	EngineConfig_GetDefault_static = EngineConfig_GetDefault() as EngineConfig;
});

export function EngineConfig_GetDefault_Remote() {
	const result = EngineConfig_GetDefault();
	//result.alarms.alarms = result.alarms.alarms.filter(a=>a.type == PromptType.Memory);
	return result;
}

// we define defaults here rather than with class field-initializers, because some of these default-values are very expansive (eg. snoozeAndPrompts.prompts array)
export function EngineConfig_GetDefault(): Omit<EngineConfig, "creator" | "createdAt" | "editedAt" | "name"> {
	return {
		id: "", // fine?

		variants: {
			variants: [
				new EngineConfigVariant({name: "Base", enabled: true}),
			],
		},

		general: {
			initialDelay: 1,
			autoEnd_enabled: false,
			autoEnd_type: AutoEndType.AfterDuration,
			autoEnd_time: 0,
			autoEnd_delay: 0,
			sleepBlockers_enabled: true,
			startupScriptTag: null,
			defaultVoice_soundTag: "",
			defaultVoice_volumeMultiplier: 1,
			globalVolumeMultiplier: new Setting([1]),
			globalPanningTypeFallback: SoundPanningType.none,
			globalMutingTypeFallback: SoundMutingType.none,
			globalAlarmRestartIntervalMultiplier: new Setting([1]),
			mouseMove_minDistToTrigger: 3,
			mouseMove_compareWindow: 500,
			mouseMoveRepeatReject_enabled: true,
			mouseMoveRepeatReject_roundPositionsTo: 1,
			sessionUI_hideStatusBar: true,
			sessionUI_hideNavBar: true,
			lockOverlay_hideStatusBar: true,
			lockOverlay_hideNavBar: true,
			sessionCrashCheck_enabled: true,
			sessionCrashCheck_interval: 5, // in minutes
			notes: "",
		},

		modeSwitcher: {
			enabled: false,
			cycleActiveComp_triggerSet: new TriggerSet({sequences: [
				/*new Sequence([
					new SequenceItem({type: "KeyHold", key_name: "VolumeDown"}),
					new SequenceItem({type: "Key", key_name: "VolumeUp"}),
				], {}),*/
				new Sequence([
					new SequenceItem({type: "Keys", key_name: "@KeyName", key_extra: "VolumeDown,VolumeUp"}),
					//new SequenceItem({type: "KeyUp", key_name: "VolumeDown"}),
				], {}),
			]}),
			onAlarmWaitEnd_setActiveMode: "DreamTranscribe",
			startStates: [],
			cycleEntries: [],
		},

		background: {
			enabled: false,
			increaseVolume_triggerSet: new TriggerSet({sequences: [
				new Sequence([new SequenceItem({type: "Key", key_name: "@KeyName", key_extra: "1"})])],
			}),
			increaseVolume_amount: .05,
			decreaseVolume_triggerSet: new TriggerSet({sequences: [
				new Sequence([new SequenceItem({type: "Key", key_name: "@KeyName", key_extra: "`"})])],
			}),
			decreaseVolume_amount: .05,
			effect: new EffectPointer(),
			startVolume: .1,
			effectsBaseEnabledUnless_microSnoozeActive: true,
			effectsBaseEnabledUnless_mediaCompPlaying: true,
			effectsBaseEnabledUnless_inPhases: [],
		},

		alerts: {
			events_addForAlertTypes: [],
			lightEffectPointer: new EffectPointer(),
			effectBaseApplyInterval: 1,
			batteryLow_threshold: 0.15,
			batteryLow_slowChargeDetection: true,
			batteryLow_lockLightsToBrightness: -1,
		},

		camera: {
			enabled: false,
			//savePath: "$SessionFolder/Camera/$TriggerTime_TriggerAt-$TriggerOffset.webm",
			preMotionBuffer: 10,
			postMotionBuffer: 60,
			//minVideoLength: 60,
			maxVideoLength: 300,

			camMotion_record: true,
			camMotion_pixelTrigger_minValueDelta: 16,
			//camMotion_frameTrigger_minTriggerPixelChainLength: 5,
			camMotion_frameTrigger_minTriggerPixelCount: 500,
			camMotion_motionTrigger_minTriggerFramePercent: .1,
			camMotion_motionTrigger_windowDuration: 5,

			eegMotion_record: true,
			eegMotion_minActivity: 5,
		},

		gyro: {
			enabled: false,
			useSampleRounding: true,

			recordData_raw: true,
			recordData_motions: true,

			detectMotion: true,
			motion_channel: GyroChannel.All_Sum,

			motion_sampleTrigger_normalizationWindowSize: 20, // can be higher than eeg window-size, since over-triggering (while adjusting) is fine
			motion_sampleTrigger_smoothing: Muse_gyroSamplesPerSecond_raw * .5,
			motion_sampleTrigger_minDeviation: 5,
			motion_motionTrigger_windowSize: .05, // resolves to 1 sample (we want gyro motion to trigger faster than eeg motion)
			motion_motionTrigger_minTriggerSamplePercent: .1,
			motion_motionTrigger_maxTriggerRate: 3,

			motion_disableEEGMotion_enabled: true,
			motion_disableEEGMotion_duration: 10,

			motion_resetEEGActivity_enabled: true,
			motion_resetEEGActivity_maxValue: 0,
		},

		eeg: {
			enabled: false,
			useSampleRounding: true,

			recordData_raw: true,
			recordData_extended: true,
			recordData_activity: true,

			detectMotion: true,

			motion_sampleTrigger_normalizationWindowSize: 10,
			motion_sampleTrigger_smoothing: Muse_eegSamplesPerSecond_raw * .5,
			motion_eegPatterns: [
				{
					enabled: true,
					width_min: .5,
					height_min: 5,
					activityIncrease: 1,
				},
			],

			motion_maxActivity: 20,
			motion_activityDecayAmount: 1,
			motion_activityDecayInterval: 20,
			motion_activityDecay_motionTriggersInterrupt: false,
			motion_activityReset_to: 0,
			motion_activityReset_at: 0,

			//motion_prompts_enabled: true,
			alarmSequence: new AlarmSequence({alarms: []}),

			/*motion_resetActivity_triggerSet: new TriggerSet({
				minTriggerInterval: 1,
				sequences: [],
			}),
			motion_snoozePrompts_triggerSet: new TriggerSet({
				minTriggerInterval: 1,
				sequences: [],
			}),
			motion_snoozePrompts_duration: 30,*/
		},

		breath: {
			enabled: false,
			recordData_raw: true,
			maxSampleGapInSequence_base: .3, // 300ms

			sync_enabled: true,
			sync_increaseCycleTime_triggerSet: new TriggerSet({sequences: [new Sequence([new SequenceItem({type: "Key", key_name: "VolumeUp"})], {})]}),
			sync_decreaseCycleTime_triggerSet: new TriggerSet({sequences: [new Sequence([new SequenceItem({type: "Key", key_name: "VolumeDown"})], {})]}),
			sync_increaseMinVolume_triggerSet: new TriggerSet({sequences: [new Sequence([new SequenceItem({type: "KeyHold", key_name: "VolumeUp"})], {})]}),
			sync_decreaseMinVolume_triggerSet: new TriggerSet({sequences: [new Sequence([new SequenceItem({type: "KeyHold", key_name: "VolumeDown"})], {})]}),
			sync_blockBreathIntervalEffects_triggerSet: new TriggerSet({sequences: []}),
			sync_blockBreathIntervalEffects_duration: 5,

			voice_volumeMultiplier: 1,
			sync_snoozeFadeOffDuration: 10,

			// ----------

			spatial_enabled: false,
			spatial_minBreathValueReversal: 0,
			spatial_inhalePanDirection: PanDirection.left,
			spatial_panStrength: 1,
			spatial_inhaleMuted: true,

			// ----------

			sync_desyncCalcType: DesyncCalcType.peaks,
			sync_minBreathPeakProminence: .15,
			sync_minBreathPeakHeight: .05,
			sync_responseTime: .1,
			sync_calcDesyncOverXSeconds_long: 20,
			sync_calcDesyncOverXSeconds_short: 10,

			sync_cycleTime: 5,
			sync_cycleTime_step: .5,
			sync_siren_cycleCorrectionInterval: 5 * 60, // every 5 minutes
			sync_sirenType: SirenType.siren,
			sync_volume_min: .1,
			sync_volume_min_step: .01,
			sync_volume_max: 1,
			sync_desyncAmountForMinVolume: .1, // there's inevitably going to be at least a little desync, so we default this to a small but non-zero value
			sync_desyncAmountForMaxVolume: 1,
			sync_memPrompt_volumeSequence: [1, .5],
			sync_memPrompt_interval: 2.5,

			//sync_light_effectPointer: new EffectPointer(),
			sync_light_brightness_min: .01,
			sync_light_brightness_max: 1,
			sync_light_desyncAmountForMinBrightness: .1, // there's inevitably going to be at least a little desync, so we default this to a small but non-zero value
			sync_light_desyncAmountForMaxBrightness: 1,

			// ----------

			dream_breathIntervalThreshold: 4,
			dream_endWhenBreathIntervalAbove: 4.5,
			dream_startWhenBreathIntervalLowRangeBelow: 3.5,
			dream_endWhenBreathIntervalLowRangeAbove: 4.25,
			dream_ender3_triggerBreathIntervalLowRange: 3.9,
			dream_ender3_stayAboveBreathIntervalLowRange: 3.5,
			dream_ender3_stayAboveDuration: 5,
			dream_endAfter: 45,
			dream_light_brightness: 1,

			// ----------

			longInhaleDetectionConfigs: [
				new LongInhaleDetectionConfig({minDuration: 4, minDuration_phaseDreaming: 3, minAscendHeight: 1, maxRoughness: .5}),
			],

			// ----------

			deild_attemptStart_triggerSet: new TriggerSet({sequences: [
				new Sequence([new SequenceItem({type: "LongInhale"})], {}),
			]}),
			deild_attemptStart_extraEffect: new EffectPointer({
				tts: {text: "$hour", volume: 0.1},
			}),
			deild_attemptDuration: 15,

			// ----------

			alert_beltDC_enabled: true,
			alert_beltDC_lockLightsToBrightness: 1,
			alert_beltLoose_enabled: true,
			alert_beltLoose_detectionStart: 1,
			alert_beltLoose_warnInterval: 60,
			alert_beltLoose_lockLightsAfter: 5,
			alert_beltLoose_lockLightsToBrightness: 1,
		},

		alarms: {
			enabled: true,
			voiceSoundTag: "",
			volumeMultiplier: 1,

			signalAwakeness_triggerSet: new TriggerSet({sequences: [
				new Sequence([new SequenceItem({type: "Key", key_name: "VolumeDown"})], {}),
				new Sequence([new SequenceItem({type: "Key", key_name: "VolumeUp"})], {}),
				new Sequence([new SequenceItem({type: "KeyHold", key_name: "VolumeDown"})], {}),
				new Sequence([new SequenceItem({type: "KeyHold", key_name: "VolumeUp"})], {}),
			]}),
			signalAwakeness_solveProgress: true,

			phaseSleep_soundTag: "",
			phaseSleep_lightTag: "",
			drift_alarmAfterDelay_enabled: true,
			alarmDelay_pool: [80],
			alarmDelay_poolRates: {80: 1},
			alarmDelay_lockPerSession: true,
			alarmDelay_optionCatchUpRate: 1,

			phaseAlarm_sequences: [
				new AlarmSequence({
					alarms: [
						new EngineConfig_SoundAlarm({
							type: AlarmType.Sound,
						}),
					],
				}),
			],
			phaseAlarm_sequences_lockPerSession: true,

			phaseSolving_soundTag: "",
			phaseSolving_lightTag: "",
			solveSuccess_message: "",
			solveSuccess_soundPath: "",
			solveSuccess_soundVolume: 1,
			phaseAfterSolveFail: Phase.drifting,
		},

		phases: {
			enabled: true,
			goPhaseTech_triggerSet: new TriggerSet({sequences: []}),
			goPhaseDrifting_triggerSet: new TriggerSet({sequences: []}),
			goPhaseDreaming_triggerSet: new TriggerSet({sequences: []}),
			goPhaseSolving_triggerSet: new TriggerSet({sequences: []}),
			goPhaseSolving_duration: 5,
			goPhaseDelving_triggerSet: new TriggerSet({sequences: []}),
			stopEnterEffectsWhenPhaseIsLeft: true,

			phaseAfterInitialDelay: Phase.dreaming,

			tech_enterEffect: new EffectPointer(),
			tech_expire_after: 40,
			tech_expire_toPhase: Phase.drifting,

			delving_enterEffect: new EffectPointer(),
		},

		microSnooze: {
			enabled: false,
			microSnooze_triggerSet: new TriggerSet({sequences: []}),
			tiltSnooze_enabled: false,
			tiltSnooze_minAngleFromFlat: 45,
			tiltSnooze_maxTiltDuration: 300,
			tiltSnooze_soundPath: "",
			tiltSnooze_soundVolume: 1,
			maxTimeForProgress: 10,
			phaseAfterSolveFail_overrides: {},
		},

		memory: {
			enabled: false,
			startPoint: 5 * 60,

			intensityStart: .1,
			intensityMax: 1,
			intensityIncreasePerStep: .1,
			stepSize: 10,

			optionPoolSize: 5,
			optionPoolOrdering: OptionPoolOrdering.Alphabetically,
			optionPoolSource: OptionPoolSource.Geography,
			optionPoolLayers: [
				new OptionPoolLayer({}, "1", "2", "3", "4", "5", "6", "7", "8", "9"),
				new OptionPoolLayer({enabled: false}, "youtube", "facebook", "twitter", "reddit", "dreamviews"),
				new OptionPoolLayer({enabled: false}, "rabbit", "elephant", "giraffe", "zebra", "bird"),
			],
			optionPool_countriesEnabled: ["China", "India", "United States", "Indonesia", "Brazil"], // five most populous countries

			minHintInterval: 10,
			nextHint_triggerSet: new TriggerSet({sequences: [
				new Sequence([new SequenceItem({type: "Key", key_name: "VolumeUp"})], {screenState: "off"})],
			}),
			previousHint_triggerSet: new TriggerSet({sequences: [
				new Sequence([new SequenceItem({type: "Key", key_name: "VolumeDown"})], {screenState: "off"})],
			}),
			giveUp_triggerSet: new TriggerSet({
				//sequences: [new Sequence([new SequenceItem({type: "KeyHold", key_name: "VolumeDown"})], {screenState: "off"})],
				sequences: [new Sequence([], {screenState: "off"})],
			}),
			targetMiss_snoozePenalty: 30,
			targetMiss_snoozeMin: 3 * 60,

			enumerationSubmission: {
				enabled: false,

				submitPick_triggerSet: new TriggerSet({sequences: [
					new Sequence([new SequenceItem({type: "KeyHold", key_name: "VolumeUp"})], {screenState: "off"})],
				}),

				nextOption_triggerSet: new TriggerSet({sequences: [
					new Sequence([new SequenceItem({type: "Key", key_name: "VolumeUp"})], {screenState: "off"})],
				}),
				previousOption_triggerSet: new TriggerSet({sequences: [
					new Sequence([new SequenceItem({type: "Key", key_name: "VolumeDown"})], {screenState: "off"})],
				}),
				changePick_snooze_enabled: true,
				changePick_snooze_duration: 10,
				minPickInterval: 5,
			},
			speechMatchSubmission: {
				enabled: true,

				remote_recordAudioOnRemote: false,

				listen_backgroundSoundTag: "",
				listen_backgroundSoundVolume: 0,

				listenOrSubmitPick_triggerSet: new TriggerSet({sequences: [
					new Sequence([new SequenceItem({type: "KeyHold", key_name: "VolumeUp"})], {screenState: "off"})],
				}),
				validTermSensitivity: 10,
				//maxDelayBetweenItems: 1,
				maxCharsBetweenItems: 10,
			},
		},

		memoryPalace: {
			enabled: false,

			nextPrompt_triggerSet: new TriggerSet({sequences: []}),
			prevPrompt_triggerSet: new TriggerSet({sequences: []}),
			voice_volumeMultiplier: 1,
			prompt_chanceForMove: .1,
			prompt_movePhrase: "Move the $target",
			targetPool: [],
			selectionType: PoolEntrySelectionType.sequential,
		},

		vr: {
			enabled: false,

			headsetOn_effect: new EffectPointer(),
			headsetOff_effect: new EffectPointer(),
			lieCatch_effect: new EffectPointer(),
			falseAccuse_effect: new EffectPointer(),

			dreams_count: 10,
			dreams_flawed: 3,
			dreams_events_min: 5,
			dreams_events_max: 10,
			dreams_flawType: DreamFlawType.omitWordInVoice,

			frames_size: .7,
			frames_duration: 3,
			frames_flickerOnDuration: 0,
			frames_flickerOffDuration: 0,
			frames_timeCreditTarget: 60 * minuteInMS,
			frames_timeCreditMarkerInterval: 30 * minuteInMS,
			frames_timeCreditLimitPerFrame: 10,
			frames_voice_volumeMultiplier: 1,
			frames_voice_speedMultiplier: 1,
			frames_imageFolder: "Pictures/LF/Frames/",
			frames_audioFolder: "Alarms/LF/FramesAudio/",
			frames_videoFolder: "Movies/LF/FramesVideo/",
			frames_pools: [
				new FramePool(),
			],

			solver_enabled: true,
			solver_minFlawFinds: 5,
			solver_markProgressBeforeCompletion: true,
			solver_maxTimeForProgress: 30,
		},

		dreamTranscribe: {
			enabled: false,
			startTranscribeForceEnable_triggerSet: new TriggerSet({sequences: [
				new Sequence([new SequenceItem({type: "KeyDown", key_name: "VolumeUp"})], {}),
			]}),
			stopTranscribeForceEnable_triggerSet: new TriggerSet({sequences: [
				new Sequence([new SequenceItem({type: "KeyUp", key_name: "VolumeUp"})], {}),
			]}),

			transcribeBaseEnabledIf_microSnoozeActive: true,
			transcribeBaseEnabledIf_inPhases: [],

			transcribeTarget: TranscribeTarget.panel,
			markTimeEveryXMinutes: 5,
			timestamps_liteMarkersInBetween: true,
			bracketWordsNearTTS: 1,
			bracketWordsNearTTS_noSpeakCountOrVibrate: true,
			bracketWordsNearTTS_noDJ: true,
			onTranscribe_speakWordCount: false,
			onTranscribe_vibrateDuration: 0,
			onTranscribe_vibrateStrength: "light",

			solver_enabled: true,
			solver_minWords: 5,
			solver_ignoreWordsNearTTS: 1,
			solver_wordSequence: "",
			solver_minTime: 0,
			solver_maxTimeValuePerWord: 1,
			solver_markProgressBeforeCompletion: true,
			solver_maxTimeForProgress: 30,
		},

		dreamNarration: {
			enabled: false,
			playAudioOnRemote: false,
			startVolume: 0,
			voiceSoundTag: "",

			/* Example result
				==========
				2019, July 4, 14:06
				Dream title: "Something something"
				Segment 1: "some1; some2; some3 some4; some5" Long text: "Something happened then this other thing."
				Segment 2: "some1; some2; some3 some4; some5"*/
			narrationTemplate: `
				if (dream.tags.includes("@NoNarrate")) return false;
				return \`
					\${date.format("YYYY-MM-DD")} at \${date.format("HH:mm")}
					Title: \${dream.title}
					\${dream.segments.map((segment, index)=>{
						let segmentNarration = \`Segment \${index + 1}: \`;
						if (segment.shortText) {
							segmentNarration += \`\${segment.shortText}\`;
						}
						if (segment.longText) {
							segmentNarration += \` Long text: \${segment.longText}\`;
						}
						return segmentNarration;
					}).join("\n")}
				\`;
			`.AsMultiline(0),

			repeatDream_triggerSet: new TriggerSet({sequences: [
				new Sequence([new SequenceItem({type: "KeyHold", key_name: "VolumeUp"})], {screenState: "off"}),
			]}),
			nextDream_triggerSet: new TriggerSet({sequences: [
				new Sequence([new SequenceItem({type: "Key", key_name: "VolumeUp"})], {screenState: "off"}),
			]}),
			previousDream_triggerSet: new TriggerSet({sequences: [
				new Sequence([new SequenceItem({type: "Key", key_name: "VolumeDown"})], {screenState: "off"}),
			]}),
			stopNarration_triggerSet: new TriggerSet({sequences: [
				new Sequence([new SequenceItem({type: "KeyHold", key_name: "VolumeDown"})], {screenState: "off"}),
			]}),
			increaseVolume_triggerSet: new TriggerSet({sequences: [
				new Sequence([new SequenceItem({type: "Key", key_name: "VolumeUp"})], {screenState: "on"}),
			]}),
			decreaseVolume_triggerSet: new TriggerSet({sequences: [
				new Sequence([new SequenceItem({type: "Key", key_name: "VolumeDown"})], {screenState: "on"}),
			]}),

			increaseVolume_amount: .1,
			decreaseVolume_amount: .1,
			volumeChangePreviewText: "New volume",
		},

		dreamRecall: {
			enabled: true,
			prevDream_triggerSet: new TriggerSet({sequences: [
				new Sequence([new SequenceItem({type: "Key", key_name: "VolumeUp"})], {}),
			]}),
			nextDream_triggerSet: new TriggerSet({sequences: [
				new Sequence([new SequenceItem({type: "Key", key_name: "VolumeDown"})], {}),
			]}),
			randomDream_triggerSet: new TriggerSet({sequences: [
				new Sequence([new SequenceItem({type: "KeyHold", key_name: "VolumeUp"})], {}),
			]}),
			lastDream_triggerSet: new TriggerSet({sequences: [
				new Sequence([new SequenceItem({type: "KeyHold", key_name: "VolumeDown"})], {}),
			]}),
			stopVoice_triggerSet: new TriggerSet({sequences: [
			],
			}),

			dreamNarration_enabled: true,
			dreamNarration_voiceSoundTag: "",
			dreamNarration_volumeMultiplier: 1,
			dreamNarration_segmentMinEntities: 1,
		},

		dreamQuiz: {
			enabled: true,
			submitPick_triggerSet: new TriggerSet({sequences: [
				new Sequence([new SequenceItem({type: "KeyHold", key_name: "VolumeUp"})], {}),
			]}),
			nextOption_triggerSet: new TriggerSet({sequences: [
				new Sequence([new SequenceItem({type: "Key", key_name: "VolumeUp"})], {}),
			]}),
			prevOption_triggerSet: new TriggerSet({sequences: [
				new Sequence([new SequenceItem({type: "Key", key_name: "VolumeDown"})], {}),
			]}),
			skipPick_triggerSet: new TriggerSet({disabled: true, sequences: [
				new Sequence([new SequenceItem({type: "KeyHold", key_name: "VolumeDown"})], {}),
			]}),

			voiceSoundTag: "",
			volumeMultiplier: 1,
			targetCountPerCycle: [],
			optionCount: 4,
			startAtLast: false,

			lucidSegmentsOnly: false,
			segmentMinEvents: 10,
			eventMaxWords: 7,
			hintSize: 2,
			targetSize: 2,
			minDistToTarget: 0,
			questionType: DreamQuiz_QuestionType.isPresent,
			questionType_invert: false,

			soundQuiz_enabled: false,
			quizRatio_dream: 1,
			quizRatio_sound: 1,
			soundFolder: "",
			soundVolume: 1,
			soundDurationLimit: 10,
		},

		conceptLink: {
			enabled: true,
			nextSequence_triggerSet: new TriggerSet({sequences: [
				new Sequence([new SequenceItem({type: "Key", key_name: "VolumeDown"})], {}),
			]}),
			prevSequence_triggerSet: new TriggerSet({sequences: [
				new Sequence([new SequenceItem({type: "KeyHold", key_name: "VolumeDown"})], {}),
			]}),
			playAudio_triggerSet: new TriggerSet({sequences: [
				new Sequence([new SequenceItem({type: "Key", key_name: "VolumeUp"})], {}),
			]}),
			randomizeAudio_triggerSet: new TriggerSet({sequences: [
				new Sequence([new SequenceItem({type: "KeyHold", key_name: "VolumeUp"})], {}),
			]}),

			voiceSoundTag: "",
			volumeMultiplier: 1,

			lucidSegmentsOnly: false,
			segmentMinEvents: 10,
			eventMaxWords: 7,
			sequenceSize: 2,
			recencyBoosts: {},
			cueRC_chance: 0,
			cueRC_chanceAsBuildup: false,

			soundFolder: "Alarms/Visualizers",
			soundVolume: 1,
			soundDurationLimit: 10,
			effect2: new EffectPointer(),
			effect2_delay: 0,
			effect2_chance: 0,
			effect2_chanceAsBuildup: false,

			solver_enabled: true,
			solver_minSequences: 1,
			solver_requireAudioPlay: false,
			solver_markProgressBeforeCompletion: true,
			solver_maxTimeForProgress: 30,
		},

		realityCheck: {
			enabled: true,
			signalRC_triggerSet: new TriggerSet({sequences: [
				new Sequence([new SequenceItem({type: "Key", key_name: "VolumeUp"})], {}),
			]}),
			signalRC_effect: new EffectPointer(),
			cueRC_triggerSet: new TriggerSet({sequences: [
				new Sequence([new SequenceItem({type: "Key", key_name: "VolumeDown"})], {}),
			]}),
			cueRC_effect: new EffectPointer(),
			waker_effect: new EffectPointer(),
			reminder_minDelay: 0,
			reminder_maxDelay: 1 * 60 * 60, // 1 hour
			reminder_recordPlayEvents: true,
			reminder_maxAmbientNoise: .0002,
			reminder_effect: new EffectPointer(),
		},

		signals: {
			enabled: true,
			signals: [],
		},

		storyExplore: {
			enabled: true,
			prevText_triggerSet: new TriggerSet({sequences: [
				new Sequence([new SequenceItem({type: "Key", key_name: "VolumeDown"})], {}),
			]}),
			nextText_triggerSet: new TriggerSet({sequences: [
				new Sequence([new SequenceItem({type: "Key", key_name: "VolumeUp"})], {}),
			]}),
			selectText_triggerSet: new TriggerSet({sequences: [
				new Sequence([new SequenceItem({type: "KeyHold", key_name: "VolumeUp"})], {}),
			]}),
			stopVoice_triggerSet: new TriggerSet({sequences: [
				new Sequence([new SequenceItem({type: "KeyHold", key_name: "VolumeDown"})], {screenState: "off"}),
			]}),
			newStory_triggerSet: new TriggerSet({sequences: [
				new Sequence([new SequenceItem({type: "KeyHold", key_name: "VolumeDown"})], {screenState: "on"}),
			]}),

			voiceSoundTag: "",
			volumeMultiplier: 1,

			minNumberedListSize: 12,
			dynamicListNumbers: [],
			llmHistory_keepLastXNumberedLists: 3,
			temperature: 1,
			thinkingType: ThinkingType.adaptive,
			thinking_budgetTokens: 10000,
			responseTokens: 4096,
			effort: EffortLevel.high,
			startPrompt: prompt_storyExplore_1,
		},

		visualization: {
			enabled: true,
			startForceEnable_triggerSet: new TriggerSet({sequences: [
				new Sequence([new SequenceItem({type: "KeyHold", key_name: "VolumeUp"})], {}),
			]}),
			startForceEnable_expireAfterOverride: null,
			stopForceEnable_triggerSet: new TriggerSet({sequences: [
				new Sequence([new SequenceItem({type: "KeyHold", key_name: "VolumeDown"})], {}),
			]}),
			stopForceEnabled_silently: false,
			toggleForceEnable_triggerSet: new TriggerSet({sequences: []}),
			toggleForceEnable_expireAfterOverride: null,
			nextPromptPool_triggerSet: new TriggerSet({sequences: []}),
			increaseTargetGridGen_triggerSet: new TriggerSet({sequences: []}),
			decreaseTargetGridGen_triggerSet: new TriggerSet({sequences: []}),
			increaseMaxGridGenDistance_triggerSet: new TriggerSet({sequences: []}),
			decreaseMaxGridGenDistance_triggerSet: new TriggerSet({sequences: []}),
			increaseInterval_triggerSet: new TriggerSet({sequences: [
				new Sequence([new SequenceItem({type: "Key", key_name: "VolumeUp"})], {}),
			]}),
			decreaseInterval_triggerSet: new TriggerSet({sequences: [
				new Sequence([new SequenceItem({type: "Key", key_name: "VolumeDown"})], {}),
			]}),
			increaseRevolutionDurations_triggerSet: new TriggerSet({sequences: []}),
			decreaseRevolutionDurations_triggerSet: new TriggerSet({sequences: []}),
			promptingBaseEnabledUnless_microSnoozeActive: true,
			promptingBaseEnabledUnless_mediaCompPlaying: true,
			promptingBaseEnabledUnless_inPhases: [Phase.initialDelay, Phase.dreaming],
			forceEnabled_expireAfter: 50,
			forceEnabled_expireSilently: true,
			promptInterval: 3,
			promptInterval_step: .5,
			promptPools: [
				new PromptPool({
					type: VisualizationPromptPoolType.brysbaert,
				}),
			],
		},

		media: {
			enabled: true,
			jumpForward_triggerSet: new TriggerSet({sequences: []}),
			jumpBackward_triggerSet: new TriggerSet({sequences: []}),
			increasePlaySpeed_triggerSet: new TriggerSet({sequences: []}),
			decreasePlaySpeed_triggerSet: new TriggerSet({sequences: []}),
			togglePlaying_triggerSet: new TriggerSet({sequences: []}),
			markDesyncCaught_triggerSet: new TriggerSet({sequences: []}),
			jumpSeconds: 5,
			speedStep: .1,
			desync_enabled: true,
			desync_startTimeMin: 5,
			desync_startTimeMax: 15,
			desync_panDelayMin: 1,
			desync_panDelayMax: 1,
			mediaPools: [],
		},

		journeyTraining: {
			enabled: true,
			entityTags: [],
			entityMinCount: 1,
			seeds_basePerEntity: 1,
			seeds_addFromDreams: false,

			voiceSoundTag: "",
			volumeMultiplier: 1,
			prefixText: "",
			postfixText: "",
		},

		journeyVisualization: {
			enabled: true,
			userNudge_triggerSet: new TriggerSet({
				//minTriggerInterval: 1000,
				sequences: [new Sequence([new SequenceItem({type: "MouseMove"})], {})],
			}),
			userNudge_soundTag: "",

			voiceSoundTag: "",
			volumeMultiplier: 1,
			entityTags: [],
			seeds_orderKey: "order",
			voiceEntityNumberInCycle: true,

			speakInterval: 1000,
			cycleReverse_minTime: 5000,
			cycleReverse_maxTime: 60000,
			userReversalDetection_maxDelay: 3000,
			userReversalDetectionFail_waitPeriod: 10000,
		},

		journeyGrid: {
			enabled: true,
			listenStart_triggerSet: new TriggerSet({sequences: [
				new Sequence([new SequenceItem({type: "KeyDown", key_name: "VolumeUp"})], {}),
			]}),
			listenEnd_triggerSet: new TriggerSet({sequences: [
				new Sequence([new SequenceItem({type: "KeyUp", key_name: "VolumeUp"})], {}),
			]}),

			entityTags_regular: ["person", "object", "concept"],
			entityTags_target: ["lucid"],
			entityTags_success: ["grid-success"],

			voiceSoundTag: "",
			volumeMultiplier: 1,
			speakInterval: 2000,

			targetDelay_minTime: 6000,
			targetDelay_maxTime: 20000,
			userTargetDetection_maxDelay: 6000,
		},

		journeyEntry: {
			enabled: true,
			prevEntity_triggerSet: new TriggerSet({sequences: [
				new Sequence([new SequenceItem({type: "Key", key_name: "VolumeUp"})], {}),
			]}),
			nextEntity_triggerSet: new TriggerSet({sequences: [
				new Sequence([new SequenceItem({type: "Key", key_name: "VolumeDown"})], {}),
			]}),

			voiceSoundTag: "",
			volumeMultiplier: 1,
		},
	};
}